import { Check, Clock, Star } from "lucide-react";
import type { VideoInterface } from "../../types/video.interface";
interface VideoCardProps {
  video: VideoInterface;
  completed?: boolean;
}

const getCategoryColors = (category: string) => {
  const colors = {
    move: 'bg-move text-white',
    eat: 'bg-eat text-white',
    play: 'bg-play text-white',
    relax: 'bg-relax text-white',
    prosper: 'bg-prosper text-black'
  };
  return colors[category.toLowerCase() as keyof typeof colors] || 'bg-holos text-white';
};

export default function VideoCard({ video, completed }: VideoCardProps) {
  const categoryName = video.saasCategory?.name || 'Move';
  const categoryColors = getCategoryColors(categoryName);
  const adjustedRating = typeof video.rating === 'number'
    ? Math.min(5, Math.max(1, video.rating + 1))
    : undefined;

  return (
    <div className="mb-4">
      <div className="h-[375px] bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden transition-transform hover:translate-y-[-2px] hover:shadow-lg">
        <a href={`/user/clases-online/video/${video.slug}`} className="block">
          <div
            className="relative w-full h-40 bg-cover bg-center"
            style={{ backgroundImage: `url(${video.videoThumbnail || 'https://image.beholos.com/fit-in/525x240/img/covers/12formassenc.jpg'})` }}
          >
            {completed && (
              <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-holos-dark text-white flex items-center justify-center z-10">
                <Check className="w-4 h-4" />
              </div>
            )}

            <div className="absolute bottom-2 left-2 flex items-center text-white bg-black bg-opacity-50 rounded px-2 py-1">
              <Clock className="w-4 h-4 mr-1" />
              <span className="text-xs">{video.duration} min</span>
            </div>

            <div className={`absolute top-2 right-2 flex items-center rounded-full px-3 py-1 ${categoryColors}`}>
              <div className="text-xs font-medium uppercase mr-1 text-black">
                {categoryName.toUpperCase()}
              </div>
              <img
                src={`https://cache.beholos.com/img/fitness-pass-v2/corporate/saas-cat-${categoryName.toLowerCase()}.svg`}
                alt={categoryName}
                className="h-4 w-4"
              />
            </div>
          </div>
        </a>

        <div className="p-3">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-holos-dark font-normal uppercase flex-1 text-left">
              {video.studio?.name}
            </p>
            {video.studio?.country?.image && <img
              alt="country"
              src={`https://cache.beholos.com${video.studio?.country?.image}`}
              className="w-6 h-4"
            />}
          </div>

          <div className="text-md font-medium mb-1 font-juana leading-tight line-clamp-2">
            {video.title}
          </div>

          <div className="flex items-center mb-2">
            <span className="text-sm font-medium">{video.teacher}</span>
          </div>

          <div className="flex items-center py-2">
            <Star className="w-4 h-4 mr-1 fill-current" />
            <strong className="mr-1">{adjustedRating?.toFixed(1) || '4.9'}</strong>
            <small className="text-gray-600">({video.ratingCount || '14'} Opiniones)</small>
          </div>

          <div className="flex items-center text-xs mb-1">
            <span className="font-medium text-sm mr-2">Dificultad</span>
            {Array.from({ length: video.intensity || 1 }).map((_, idx) => (
              <img
                key={idx}
                className="mr-1 w-4 h-4"
                src="https://cache.beholos.com/img/fitness-pass-v2/global/flame-icon.svg"
                alt="intensity"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}